import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import PaywallOverlay from "../components/PaywallOverlay";
import { useNavigate } from "react-router-dom";

const PaywallContext = createContext(null);

export function PaywallProvider({ children }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [redirectPath, setRedirectPath] = useState(null);
  // SEC-C3: owner-inactive notice (never shows paywall for clients)
  const [ownerInactiveMsg, setOwnerInactiveMsg] = useState(null);

  const showPaywall = useCallback((r = "") => {
    setReason(r);
    setOpen(true);
  }, []);

  // Listen for the global 402 event fired by the API interceptor
  useEffect(() => {
    const handler = (e) => showPaywall(e.detail?.reason || "");
    window.addEventListener("st:paywall", handler);
    return () => window.removeEventListener("st:paywall", handler);
  }, [showPaywall]);

  // SEC-C3: Listen for owner-inactive event (403 with error_code owner_inactive)
  useEffect(() => {
    const handler = (e) => {
      setOwnerInactiveMsg(
        e.detail?.message ||
          "This project is temporarily unavailable. Please contact your contractor."
      );
    };
    window.addEventListener("st:owner-inactive", handler);
    return () => window.removeEventListener("st:owner-inactive", handler);
  }, []);

  const hidePaywall = useCallback(() => {
    setOpen(false);
    if (redirectPath) {
      navigate(redirectPath);
      setRedirectPath(null);
    }
  }, [redirectPath]);

  const paywallValue = {
    showPaywall,
    hidePaywall,
    paywallOpen: open,
    ownerInactiveMsg,
    dismissOwnerInactive: () => setOwnerInactiveMsg(null),
    setPaywallRedirectPath: (path) => setRedirectPath(path),
  };

  return (
    <PaywallContext.Provider value={{ ...paywallValue }}>
      {children}
      <PaywallOverlay
        open={open}
        reason={reason}
        onClose={hidePaywall}
      />
      {/* SEC-C3: Owner-inactive inline notice for clients */}
      {ownerInactiveMsg && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-md mx-4 text-center space-y-4">
            <div className="text-4xl">🔒</div>
            <h2 className="text-lg font-semibold text-slate-800">Project Unavailable</h2>
            <p className="text-sm text-slate-600">{ownerInactiveMsg}</p>
            <button
              onClick={() => { setOwnerInactiveMsg(null); navigate("/dashboard"); }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-500"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const ctx = useContext(PaywallContext);
  if (!ctx) throw new Error("usePaywall must be used inside PaywallProvider");
  return ctx;
}
